import React, { useState } from 'react';
import { X, Send, Loader } from 'lucide-react';

const PackageRequestModal = ({ package: pkg, onClose }) => {
  const [formData, setFormData] = useState({
    packageId: pkg?.id || '',
    packageName: pkg?.nameAr || '',
    clientName: '',
    phone: '',
    email: '',
    clientType: 'individual',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/package-requests`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...formData,
          requestDate: new Date().toISOString(),
          status: 'pending'
        })
      });

      if (response.ok) {
        setSubmitStatus('success');
        setTimeout(() => {
          onClose();
        }, 2000);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting package request:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" dir="rtl">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className={`bg-gradient-to-r ${pkg?.color || 'from-amber-500 to-amber-600'} text-white p-6 relative`}>
          <button
            onClick={onClose}
            className="absolute left-4 top-4 hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all"
          >
            <X size={24} />
          </button>
          <h2 className="text-2xl font-bold mb-2">
            {pkg?.custom ? 'صمّم باقتك المخصصة' : `طلب باقة ${pkg?.nameAr}`}
          </h2>
          <p className="text-sm opacity-90">
            {pkg?.custom 
              ? 'أخبرنا باحتياجاتك وسنصمم لك باقة مخصصة تماماً'
              : 'املأ البيانات التالية وسنتواصل معك في أقرب وقت'}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Package Name (Read-only) */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              الباقة المختارة
            </label>
            <input
              type="text"
              value={formData.packageName}
              readOnly
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl bg-gray-50 text-gray-700"
            />
          </div>

          {/* Client Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              الاسم / اسم المنشأة <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="clientName"
              value={formData.clientName}
              onChange={handleChange}
              required
              placeholder="أدخل الاسم أو اسم الشركة"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              رقم الجوال <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              required
              placeholder="05xxxxxxxx"
              pattern="[0-9]{10}"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              البريد الإلكتروني <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="example@domain.com"
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
            />
          </div>

          {/* Client Type */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              نوع العميل <span className="text-red-500">*</span>
            </label>
            <select
              name="clientType"
              value={formData.clientType}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none"
            >
              <option value="individual">فرد</option>
              <option value="company">شركة</option>
              <option value="entity">جهة</option>
            </select>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              {pkg?.custom 
                ? 'وصف احتياجاتك القانونية (مهم للباقة المخصصة)'
                : 'ملاحظات أو وصف مختصر للنشاط'}
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              placeholder={pkg?.custom 
                ? 'مثال: نحتاج 10 استشارات شهرياً، مراجعة 5 عقود، دعم موارد بشرية، حضور محامي مرتين شهرياً...'
                : 'اكتب أي ملاحظات إضافية أو وصف موجز لنشاطك'}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-amber-500 focus:outline-none resize-none"
            />
            {pkg?.custom && (
              <p className="text-xs text-gray-500 mt-2">
                * كلما كانت التفاصيل أوضح، كلما كان العرض أدق وأسرع
              </p>
            )}
          </div>

          {/* Submit Status Messages */}
          {submitStatus === 'success' && (
            <div className="bg-green-50 border-2 border-green-500 text-green-800 p-4 rounded-xl">
              <p className="font-semibold">✓ تم إرسال طلبك بنجاح!</p>
              <p className="text-sm mt-1">سنتواصل معك في أقرب وقت ممكن.</p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="bg-red-50 border-2 border-red-500 text-red-800 p-4 rounded-xl">
              <p className="font-semibold">✗ حدث خطأ في إرسال الطلب</p>
              <p className="text-sm mt-1">الرجاء المحاولة مرة أخرى أو التواصل معنا مباشرة.</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex gap-4">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`flex-1 bg-gradient-to-r ${pkg?.color || 'from-amber-500 to-amber-600'} text-white py-3 rounded-xl font-bold hover:shadow-lg transition-all flex items-center justify-center gap-2 ${
                isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              {isSubmitting ? (
                <>
                  <Loader className="animate-spin" size={20} />
                  جاري الإرسال...
                </>
              ) : (
                <>
                  إرسال الطلب
                  <Send size={20} />
                </>
              )}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:border-gray-400 transition-all"
            >
              إلغاء
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PackageRequestModal;
